import React, { useEffect, useState } from 'react';
import { uploadImage } from '../firebase';
import Notification from './Notification';
import Spinner from './Spinner';
import './Products.css';

// Encuadre: dónde se “ancla” la imagen dentro del recuadro (como foto de perfil)
const POSITION_GRID = [
  { value: '0% 0%', title: 'Arriba izquierda' },
  { value: '50% 0%', title: 'Arriba centro' },
  { value: '100% 0%', title: 'Arriba derecha' },
  { value: '0% 50%', title: 'Centro izquierda' },
  { value: '50% 50%', title: 'Centro' },
  { value: '100% 50%', title: 'Centro derecha' },
  { value: '0% 100%', title: 'Abajo izquierda' },
  { value: '50% 100%', title: 'Abajo centro' },
  { value: '100% 100%', title: 'Abajo derecha' },
];
const DEFAULT_POSITION = '50% 50%';

const DESCRIPTION_MAX_LENGTH = 2000;

const ProductForm = ({ product, onSubmit, onCancel, categories, setCategories }) => {
  const [model, setModel] = useState(product?.model || '');
  const [price, setPrice] = useState(product?.price || '');
  const [description, setDescription] = useState(product?.description || '');
  const [selectedCategories, setSelectedCategories] = useState(() => {
    if (product?.categories && Array.isArray(product.categories) && product.categories.length > 0) return [...product.categories];
    if (product?.category) return [product.category];
    return [];
  });
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState(product?.images || []);
  const [loading, setLoading] = useState(false);
  const [stock, setStock] = useState(product?.stock || 0);
  const [hidden, setHidden] = useState(product?.hidden || false);
  const [discountExcluded, setDiscountExcluded] = useState(product?.discountExcluded ?? false);
  const [excludeFromStock, setExcludeFromStock] = useState(product?.excludeFromStock ?? false);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [imagePositions, setImagePositions] = useState([]);
  const [removingBg, setRemovingBg] = useState(false);

  // Limpiar archivos cuando se abre el formulario para un nuevo producto
  useEffect(() => {
    if (!product) {
      setFiles([]);
      setPreviews([]);
      setMainImageIndex(0);
      setImagePositions([]);
      setSelectedCategories([]);
      setShowNewCategoryInput(false);
    } else {
      setPreviews(product.images || []);
      setFiles([]);
      setMainImageIndex(0);
      if (product.categories && Array.isArray(product.categories) && product.categories.length > 0) {
        setSelectedCategories([...product.categories]);
      } else if (product.category) {
        setSelectedCategories([product.category]);
      }
      const existing = product.images || [];
      const raw = product.imagePositions || [];
      const norm = (p) => (p && String(p).includes('%')) ? p : (p === 'top' ? '50% 0%' : p === 'bottom' ? '50% 100%' : DEFAULT_POSITION);
      const normalized = raw.length === existing.length ? raw.map(norm) : existing.map(() => DEFAULT_POSITION);
      setImagePositions(normalized);
    }
  }, [product]);

  // Mantener imagePositions sincronizado con la cantidad de previews
  useEffect(() => {
    setImagePositions(prev => {
      const len = previews.length;
      if (prev.length === len) return prev;
      if (prev.length > len) return prev.slice(0, len);
      return [...prev, ...Array(len - prev.length).fill(DEFAULT_POSITION)];
    });
  }, [previews.length]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const existingImagesCount = product?.images?.length || 0;
    const totalImages = existingImagesCount + files.length + selectedFiles.length;
    
    if (totalImages > 6) {
      setNotification({ 
        message: 'Máximo 6 imágenes permitidas. Se han seleccionado demasiadas imágenes.', 
        show: true 
      });
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
      return;
    }
    
    // Filtrar archivos duplicados por nombre
    const newFiles = selectedFiles.filter(newFile => 
      !files.some(existingFile => existingFile.name === newFile.name)
    );
    
    if (newFiles.length === 0) {
      setNotification({ 
        message: 'Todas las imágenes seleccionadas ya están agregadas.', 
        show: true 
      });
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
      return;
    }
    
    setFiles([...files, ...newFiles]);
    
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    e.target.value = '';
  };

  const removeBackground = async (file) => {
    const formData = new FormData();
    formData.append('image_file', file);
    formData.append('size', 'auto');

    try {
      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: {
          'X-Api-Key': process.env.REACT_APP_REMOVE_BG_API_KEY,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to remove background');
      }

      const blob = await response.blob();
      return new File([blob], file.name, { type: 'image/png' });
    } catch (error) {
      console.error('Error removing background:', error);
      throw error;
    }
  };

  // Previsualización: mostrar imágenes existentes + todas las nuevas en cuanto se cargan
  useEffect(() => {
    if (files.length === 0) {
      setPreviews(product?.images || []);
      return;
    }
    const existingPreviews = product?.images || [];
    const createPreviews = async () => {
      const newPreviews = [...existingPreviews];
      for (let i = 0; i < files.length; i++) {
        const reader = new FileReader();
        const preview = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(files[i]);
        });
        newPreviews.push(preview);
      }
      setPreviews(newPreviews);
    };
    createPreviews();
  }, [files, product?.images]);

  const handleRemoveImage = (index) => {
    setPreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
    setImagePositions(prev => prev.filter((_, i) => i !== index));
    const existingImagesCount = product?.images?.length || 0;
    if (index >= existingImagesCount) {
      const fileIndex = index - existingImagesCount;
      setFiles(prevFiles => prevFiles.filter((_, i) => i !== fileIndex));
    }
    if (index === mainImageIndex) {
      setMainImageIndex(0);
    } else if (index < mainImageIndex) {
      setMainImageIndex(prev => prev - 1);
    }
  };

  const handleSetMainImage = (index) => {
    setMainImageIndex(index);
  };

  const handleSetPosition = (index, value) => {
    setImagePositions(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  /** Quitar fondo solo a la imagen indicada (una llamada a la API por imagen). */
  const handleRemoveBackgroundOne = async (previewIndex) => {
    const existingCount = product?.images?.length || 0;
    if (previewIndex < existingCount) return;
    const fileIndex = previewIndex - existingCount;
    if (fileIndex < 0 || fileIndex >= files.length) return;
    setRemovingBg(true);
    try {
      const processedFile = await removeBackground(files[fileIndex]);
      setFiles(prev => {
        const next = [...prev];
        next[fileIndex] = processedFile;
        return next;
      });
      const newPreview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(processedFile);
      });
      setPreviews(prev => {
        const next = [...prev];
        next[previewIndex] = newPreview;
        return next;
      });
    } catch (error) {
      console.error('Error removing background:', error);
      setNotification({ message: 'No se pudo quitar el fondo. Revisa la API key o la conexión.', show: true });
      setTimeout(() => setNotification({ message: '', show: false }), 4000);
    } finally {
      setRemovingBg(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Construir lista final desde previews actuales: respeta eliminaciones y orden
    let imageUrls = [];
    try {
      let fileIndex = 0;
      for (const p of previews) {
        if (typeof p === 'string' && (p.startsWith('http://') || p.startsWith('https://'))) {
          imageUrls.push(p);
        } else if (typeof p === 'string' && p.startsWith('data:')) {
          if (fileIndex < files.length) {
            const url = await uploadImage(files[fileIndex]);
            imageUrls.push(url);
            fileIndex++;
          }
        }
      }
    } catch (error) {
      console.error('Error uploading images to Firebase:', error);
      alert('Error uploading images, please try again.');
      setLoading(false);
      return;
    }

    // Reordenar imágenes con la principal primero (y posiciones en el mismo orden)
    let finalPositions = imagePositions.length === imageUrls.length ? [...imagePositions] : imageUrls.map(() => DEFAULT_POSITION);
    if (imageUrls.length > 0 && mainImageIndex > 0) {
      const mainImage = imageUrls[mainImageIndex];
      const otherImages = imageUrls.filter((_, index) => index !== mainImageIndex);
      imageUrls = [mainImage, ...otherImages];
      const mainPos = finalPositions[mainImageIndex];
      finalPositions = [mainPos, ...finalPositions.filter((_, i) => i !== mainImageIndex)];
    }

    const finalCategories = selectedCategories.length > 0 ? [...selectedCategories] : (newCategory.trim() ? [newCategory.trim()] : []);
    if (newCategory.trim() && !finalCategories.includes(newCategory.trim())) {
      finalCategories.push(newCategory.trim());
    }
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      setCategories([...categories, newCategory.trim()]);
    }
    if (finalCategories.length === 0) {
      setNotification({ message: 'Seleccioná al menos una categoría.', show: true });
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
      setLoading(false);
      return;
    }

    onSubmit({
      model,
      images: imageUrls,
      imagePositions: finalPositions,
      price: parseFloat(price),
      description,
      category: finalCategories[0],
      categories: finalCategories,
      stock: parseInt(stock),
      hidden,
      discountExcluded,
      excludeFromStock
    });

    setLoading(false);
    setNotification({ message: 'Producto guardado con éxito', show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  return (
    <form onSubmit={handleSubmit} className="product-form-container">
      {loading && <Spinner />}
      <Notification message={notification.message} show={notification.show} />
      <h3>{product ? 'Editar Producto' : 'Agregar Nuevo Producto'}</h3>
      <div className="form-group-container">
        <label htmlFor="model" className="form-label">Modelo</label>
        <input
          type="text"
          id="model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          required
          className="form-input"
        />
      </div>
      <div className="form-group-container">
        <label className="form-label">Imágenes (hasta 6)</label>
        <p className="form-hint">Seleccioná las imágenes. Opcionalmente podés quitar el fondo después.</p>
        <div className="file-upload-wrapper">
          <input 
            type="file" 
            className="file-upload-input"
            id="product-images-upload"
            onChange={handleFileChange} 
            accept=".jpg,.png" 
            multiple 
          />
          <label htmlFor="product-images-upload" className="file-upload-button">
            <svg className="file-upload-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            Seleccionar Imágenes
          </label>
        </div>
        <div className="image-preview-container">
          {previews.map((preview, index) => {
            const position = imagePositions[index] || DEFAULT_POSITION;
            const existingCount = product?.images?.length || 0;
            const isNewFile = index >= existingCount;
            return (
              <div
                key={index}
                className={`image-preview-wrapper ${index === mainImageIndex ? 'main' : ''}`}
              >
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="image-preview"
                  style={{ objectPosition: position }}
                />
                <button type="button" onClick={() => handleRemoveImage(index)} className="remove-image-button" title="Quitar imagen">×</button>
                <button
                  type="button"
                  onClick={() => handleSetMainImage(index)}
                  className={`set-main-image-button ${index === mainImageIndex ? 'active' : ''}`}
                  title={index === mainImageIndex ? 'Imagen principal' : 'Establecer como principal'}
                >
                  ⭐
                </button>
                {isNewFile && (
                  <button
                    type="button"
                    onClick={() => handleRemoveBackgroundOne(index)}
                    disabled={removingBg || loading}
                    className="remove-bg-one-button"
                    title="Quitar fondo a esta imagen (usa 1 crédito de la API)"
                  >
                    {removingBg ? '…' : 'Quitar fondo'}
                  </button>
                )}
                <div className="image-preview-controls">
                  <span className="position-label">Encuadre</span>
                  <div className="position-grid" role="group" aria-label="Ubicar imagen en el recuadro">
                    {POSITION_GRID.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`position-grid-cell ${position === opt.value ? 'active' : ''}`}
                        title={opt.title}
                        onClick={() => handleSetPosition(index, opt.value)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="image-counter-info">
          {previews.length} de 6 imágenes
          {previews.length > 0 && (
            <span className="main-image-info"> · Principal: #{mainImageIndex + 1}</span>
          )}
        </div>
      </div>
      <div className="form-group-container">
        <label htmlFor="price" className="form-label">Precio</label>
        <input
          type="number"
          id="price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          className="form-input"
        />
      </div>
      <div className="form-group-container">
        <label htmlFor="stock" className="form-label">Stock</label>
        <input
          type="number"
          id="stock"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          required
          className="form-input"
        />
      </div>
      <div className="form-group-container">
        <label htmlFor="description" className="form-label">Descripción</label>
        <textarea
          id="description"
          maxLength={DESCRIPTION_MAX_LENGTH}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          className="form-textarea"
        />
        <p className="form-hint">
          {description.length}/{DESCRIPTION_MAX_LENGTH} caracteres
        </p>
      </div>
      <div className="form-group-container">
        <label className="form-label">Categorías</label>
        <p className="form-hint">Podés asignar más de una. El producto aparecerá en cada una.</p>
        {selectedCategories.length > 0 && (
          <div className="form-categories-chips">
            {selectedCategories.map(cat => (
              <span key={cat} className="form-category-chip">
                {cat}
                <button
                  type="button"
                  className="form-category-chip-remove"
                  onClick={() => setSelectedCategories(prev => prev.filter(c => c !== cat))}
                  title="Quitar categoría"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="form-categories-add">
          <select
            id="category-add"
            value=""
            onChange={(e) => {
              const v = e.target.value;
              e.target.value = '';
              if (v === 'new') {
                setShowNewCategoryInput(true);
                return;
              }
              if (v && !selectedCategories.includes(v)) setSelectedCategories(prev => [...prev, v]);
            }}
            className="form-select"
          >
            <option value="">Agregar categoría...</option>
            {categories.filter(c => !selectedCategories.includes(c)).map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
            <option value="new">+ Nueva categoría</option>
          </select>
          {showNewCategoryInput && (
            <div className="form-new-category-row">
              <input
                type="text"
                placeholder="Nombre de la nueva categoría"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="form-input"
              />
              <button
                type="button"
                className="form-action-button submit"
                onClick={() => {
                  const t = newCategory.trim();
                  if (t && !selectedCategories.includes(t)) {
                    setSelectedCategories(prev => [...prev, t]);
                    if (!categories.includes(t)) setCategories(prev => [...prev, t]);
                    setNewCategory('');
                    setShowNewCategoryInput(false);
                  }
                }}
              >
                Agregar
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="form-group-container hidden-checkbox-container">
        <div className="hidden-checkbox-row">
          <label htmlFor="hidden" className="hidden-checkbox-label">
            <input
              type="checkbox"
              id="hidden"
              checked={hidden}
              onChange={(e) => setHidden(e.target.checked)}
              className="form-input"
            />
            <span className="hidden-checkbox-text">Ocultar producto</span>
          </label>
          <label htmlFor="discountExcluded" className="hidden-checkbox-label">
            <input
              type="checkbox"
              id="discountExcluded"
              checked={!discountExcluded}
              onChange={(e) => setDiscountExcluded(!e.target.checked)}
              className="form-input"
            />
            <span className="hidden-checkbox-text">Apto descuento</span>
          </label>
          <label htmlFor="excludeFromStock" className="hidden-checkbox-label">
            <input
              type="checkbox"
              id="excludeFromStock"
              checked={!excludeFromStock}
              onChange={(e) => setExcludeFromStock(!e.target.checked)}
              className="form-input"
            />
            <span className="hidden-checkbox-text">Computa stock</span>
          </label>
        </div>
      </div>
      <div className="form-actions-container">
        <button type="submit" disabled={loading} className="form-action-button submit">
          Guardar
        </button>
        <button type="button" onClick={onCancel} disabled={loading} className="form-action-button cancel">Cancelar</button>
      </div>
    </form>
  );
};

export default ProductForm;